/**
 * Display helpers of the dispatch screens (batch 8 T12c1, #3033). Display only: nothing here decides anything.
 *
 * Money in a document arrives as exact decimal TEXT in cents at four decimals (`"1234.5000"` is 1234.5 cents, 12,345 euros).
 * It is formatted from the text with BigInt, never through a float. Times arrive as ISO instants and are shown in the viewer's own
 * zone WITH the zone's name, so a time is never ambiguous.
 */

const CENTS4 = /^(\d{1,12})\.(\d{4})$/;
const PERCENT4 = /^(\d{1,3})\.(\d{4})$/;

function localeParts(language: string): { group: Intl.NumberFormat; decimal: string } {
  let group: Intl.NumberFormat;
  try {
    group = new Intl.NumberFormat(language);
  } catch {
    group = new Intl.NumberFormat('en');
  }
  const decimal = group.formatToParts(1.1).find((part) => part.type === 'decimal')?.value ?? '.';
  return { group, decimal };
}

/** `"1234.5000"` cents -> `12,345 €` (at least two, at most six decimals of a euro, non-breaking space before the symbol). */
export function formatCents4(text: string, language: string, currency = 'EUR'): string {
  const match = CENTS4.exec(text);
  if (!match) return '—';
  // units of 1/10000 cent = 1/1_000_000 euro
  const units = BigInt(match[1] ?? '0') * 10_000n + BigInt(match[2] ?? '0');
  const euros = units / 1_000_000n;
  let fraction = (units % 1_000_000n).toString().padStart(6, '0');
  while (fraction.length > 2 && fraction.endsWith('0')) fraction = fraction.slice(0, -1);
  const { group, decimal } = localeParts(language);
  const symbol = currency === 'EUR' ? '€' : currency;
  return `${group.format(euros)}${decimal}${fraction}\u00a0${symbol}`;
}

/** `"10.0000"` -> `10 %`, `"2.5000"` -> `2,5 %`. */
export function formatPercent4(text: string, language: string): string {
  const match = PERCENT4.exec(text);
  if (!match) return '—';
  let fraction = match[2] ?? '0000';
  while (fraction.length > 0 && fraction.endsWith('0')) fraction = fraction.slice(0, -1);
  const { decimal } = localeParts(language);
  return `${match[1] ?? '0'}${fraction ? `${decimal}${fraction}` : ''}\u00a0%`;
}

function dateOf(value: string | null | undefined): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatter(language: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  try {
    return new Intl.DateTimeFormat(language, options);
  } catch {
    return new Intl.DateTimeFormat('en', options);
  }
}

/** Date, time and the viewer's time zone (`timeStyle: 'long'` names it): `29.10.2026 klo 14.05.12 UTC+2`. A missing or unreadable value is an em dash. */
export function formatDateTimeZone(value: string | null | undefined, language: string): string {
  const date = dateOf(value);
  return date ? formatter(language, { dateStyle: 'medium', timeStyle: 'long' }).format(date) : '—';
}

/** A calendar date as the server stored it (`2026-10-30`), without any time-zone shift. */
export function formatCalendarDate(value: string | null | undefined, language: string): string {
  if (!value) return '—';
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return '—';
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  return Number.isNaN(date.getTime())
    ? '—'
    : formatter(language, { dateStyle: 'medium', timeZone: 'UTC' }).format(date);
}
